import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import StateObserver from 'src/StateObserver';
import { ImageScaleView, ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { trackPinchEvent } from 'src/lib/analytics/events';
import { Feature } from 'src/replicant/analytics';
import { DaycareId, DaycareOption } from 'src/replicant/daycare';
import * as Daycare from 'src/replicant/daycare';
import { getLocale } from 'src/replicant/getters/user';
import { DaycareOptionPanel } from './DaycareOptionPanel';
import { puzzleCoinsPinch } from '../puzzlecoins/puzzlecoins.sequence';

import { AssetGroup } from '@play-co/timestep-core';
import { getDaycareId } from '../../replicant/daycare/daycare.getters';

type CloseHandler = (result: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupConfirmDaycare extends PopupBasic {
  private close: CloseHandler;
  private cost: LangBitmapFontTextView;
  private balance: LangBitmapFontTextView;
  private okButton: ButtonScaleViewWithText;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      titleIcon: {
        image: 'assets/ui/daycare/icons/banner_daycare_icon.png',
      },
      tracking: {
        feature: Feature.DAYCARE._,
        $subFeature: Feature.DAYCARE.PURCHASE,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      ...ui.message,
      width: this.box.style.width - 2 * ui.message.x,
    });

    this.box.addSubview(
      new ImageScaleView({
        ...ui.underline,
        width: this.message.style.width,
      }),
    );

    const costRow = this.box.addSubview(new ImageSliceView(ui.costRow));

    costRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('careItem.pointsRequired'),
      }),
    );

    this.cost = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: costRow,
    });

    costRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        x: costRow.style.width - ui.currency.width,
      }),
    );

    const balanceRow = this.box.addSubview(new ImageSliceView(ui.balanceRow));

    balanceRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('careItem.pointsRemaining'),
      }),
    );

    this.balance = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: balanceRow,
    });

    balanceRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        x: balanceRow.style.width - ui.currency.width,
      }),
    );

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...ui.backButton,
        localeText: () => i18n('basic.back'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close(false);
        },
        tracking: {
          feature: Feature.DAYCARE._,
          $subFeature: Feature.DAYCARE.PURCHASE_BACK,
        },
      }),
    );

    this.okButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.box,
      localeText: () => i18n('basic.okay'),
      tracking: {
        feature: Feature.DAYCARE._,
        $subFeature: Feature.DAYCARE.PURCHASE_OK,
      },
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup(['assets/ui/daycare/icons/banner_daycare_icon.png']);
  }

  public init(opts: { daycareId?: DaycareId }) {
    const { user } = StateObserver.getState();
    const { daycareId = getDaycareId() } = opts;
    const daycareOption = Daycare.ruleset.daycareOptions[daycareId];
    const duration =
      getLocale(user) === 'en'
        ? daycareOption.duration_en
        : daycareOption.duration_jp;

    super.init({
      title: i18n('daycare.confirmTitle'),
      message: i18n('daycare.confirmMessage', { duration }),
    });

    this.box.addSubview(new DaycareOptionPanel({ daycareId }));
    this.cost.localeText = () => parseAmount(daycareOption.price);
    this.refreshPointsRemaining(daycareId);

    this.okButton.onClick = async () => {
      sounds.sfx.play('ok');

      const canPurchase = this.canPurchase(daycareOption);
      if (!canPurchase) {
        await this.showPinchSequence(daycareId);
        return;
      }

      this.close(true);
    };
  }

  private async showPinchSequence(daycareId: DaycareId) {
    this.fadeOut();
    this.trackPinchEvent(daycareId);
    await puzzleCoinsPinch();
    this.refreshPointsRemaining(daycareId);
    this.fadeIn();
  }

  private refreshPointsRemaining(daycareId: DaycareId) {
    const daycareOption = Daycare.ruleset.daycareOptions[daycareId];
    const balance = this.getBalanceAfterPurchase(daycareOption);
    this.balance.updateOpts({
      localeText: () => parseAmount(balance),
      color: this.canPurchase(daycareOption)
        ? ui.highlightRowValue.color
        : 'red',
    });
  }

  /**
   * Check if player can purchase care item
   */
  private canPurchase(daycareOption: DaycareOption) {
    return this.getBalanceAfterPurchase(daycareOption) >= 0;
  }

  /**
   * Get balance after purchase, can be negative
   */
  private getBalanceAfterPurchase({ price }: DaycareOption) {
    return StateObserver.getState().user.inventory.points - price;
  }

  private trackPinchEvent(daycareId: DaycareId) {
    trackPinchEvent({
      resource: 'bPoints',
      trigger: `daycare`,
      item_id: daycareId,
    });
  }
}
